import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
  Collapse,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useUserContextController } from '../../../context/UserContext';
import { secondaryActionButtonSx } from '../../modal/ModalActionButtons';
import {
  fetchSalesforceAccountProperties,
  fetchSalesforceAccountsByIds,
  fetchSalesforceContactProperties,
  fetchSalesforceContactsByIds,
  listSalesforceAccounts,
  listSalesforceContacts
} from '../../../api/connector/salesforce';
import type { SalesforceBrowseRecord } from '../../../types/salesforce';

export type SalesforceListImportType = 'ACCOUNTS' | 'CONTACTS';

interface Props {
  importType: SalesforceListImportType;
  onConfirm: (rows: any[], config?: Record<string, any>) => void;
  onCancel: () => void;
  hideCancel?: boolean;
}

const ImportSalesforceList: React.FC<Props> = ({
  importType,
  onConfirm,
  onCancel,
  hideCancel = false
}) => {
  const { showSnackbar } = useSnackbar();
  const [userContext] = useUserContextController();
  const salesforceConnection = useMemo(
    () =>
      userContext?.connectedAccounts?.find(
        (c: any) => c.app === 'salesforce_rest_api'
      ),
    [userContext]
  );
  const connectionId = salesforceConnection?.connectionId as string | undefined;

  const [items, setItems] = useState<SalesforceBrowseRecord[]>([]);
  const [offsetId, setOffsetId] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Field selection state
  const [availableProperties, setAvailableProperties] = useState<
    Record<string, string>
  >({});
  const [selectedFieldsLabel, setSelectedFieldsLabel] = useState<Set<string>>(
    new Set()
  );
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [fieldsSectionExpanded, setFieldsSectionExpanded] = useState(true);
  const [itemsSectionExpanded, setItemsSectionExpanded] = useState(true);
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const previousDebouncedSearchRef = useRef<string>('');
  const [filterByOwner, setFilterByOwner] = useState(true);
  const hasAutoSwitchedFromEmptyRef = useRef(false);

  // Configuration based on import type
  const config = useMemo(() => {
    if (importType === 'ACCOUNTS') {
      return {
        title: 'Import Accounts from Salesforce',
        description:
          'Select accounts to import. You can select multiple accounts across pages before importing.',
        emptyTitle: 'No accounts found',
        emptyMessage:
          'If you have selected "Only show my accounts", turn it off to see all accounts.',
        itemLabel: 'account',
        itemLabelPlural: 'accounts',
        fetchFn: listSalesforceAccounts,
        getPrimaryText: (item: SalesforceBrowseRecord) =>
          item.properties?.Name ?? item.properties?.name ?? 'Unnamed Account',
        getSecondaryText: (item: SalesforceBrowseRecord) =>
          item.properties?.Website ?? item.properties?.website ?? 'No website'
      };
    } else {
      return {
        title: 'Import Leads from Salesforce Contacts',
        description:
          'Select contacts to import. You can select multiple contacts across pages before importing.',
        emptyTitle: 'No contacts found',
        emptyMessage:
          'Make sure your connected Salesforce user has access to Contacts.',
        itemLabel: 'contact',
        itemLabelPlural: 'contacts',
        fetchFn: listSalesforceContacts,
        getPrimaryText: (item: SalesforceBrowseRecord) => {
          const props = item.properties || {};
          return (
            [
              props.FirstName ?? props.firstname,
              props.LastName ?? props.lastname
            ]
              .filter(Boolean)
              .join(' ') || 'Unnamed Contact'
          );
        },
        getSecondaryText: (item: SalesforceBrowseRecord) =>
          item.properties?.Email ?? item.properties?.email ?? 'No email'
      };
    }
  }, [importType]);

  const getListArgs = useMemo(
    () =>
      (overrides: {
        limit?: number;
        offsetId?: string | null;
        search?: string | null;
      }) => {
        const base = {
          limit: overrides.limit,
          offsetId: overrides.offsetId ?? undefined,
          search: overrides.search ?? undefined
        };
        if (importType === 'ACCOUNTS') {
          return { ...base, filter_by_my_owner: filterByOwner };
        }
        return base;
      },
    [importType, filterByOwner]
  );

  // Fetch available properties and initial list when connection or import type changes
  useEffect(() => {
    if (!connectionId) {
      return;
    }

    const fetchData = async () => {
      setLoadingProperties(true);
      setLoadingList(true);
      try {
        // Fetch both in parallel for better performance
        const [propertiesResult, listResult] = await Promise.allSettled([
          importType === 'ACCOUNTS'
            ? fetchSalesforceAccountProperties()
            : fetchSalesforceContactProperties(),
          config.fetchFn(getListArgs({ limit: 10 }))
        ]);

        // Handle properties result
        if (propertiesResult.status === 'fulfilled') {
          setAvailableProperties(propertiesResult.value.properties || {});
        } else {
          showSnackbar(
            propertiesResult.reason?.message ||
              'Failed to fetch available properties',
            'error'
          );
        }

        if (listResult.status === 'fulfilled') {
          let records = listResult.value.records;
          let offsetIdValue = listResult.value.offsetId ?? null;

          if (
            importType === 'ACCOUNTS' &&
            filterByOwner &&
            records.length === 0 &&
            !hasAutoSwitchedFromEmptyRef.current
          ) {
            hasAutoSwitchedFromEmptyRef.current = true;
            try {
              const allResult = await listSalesforceAccounts({
                limit: 10,
                filter_by_my_owner: false
              });
              records = allResult.records;
              offsetIdValue = allResult.offsetId ?? null;
              setFilterByOwner(false);
            } catch {
              // Keep empty list and filter on if refetch fails
            }
          }

          setItems(records);
          setOffsetId(offsetIdValue);
          setInitialLoadDone(true);

          // Set default selected fields when we have properties and at least one item
          if (propertiesResult.status === 'fulfilled' && records.length > 0) {
            const propertiesLabelToNameMap = propertiesResult.value.properties;
            const propertiesNameToLabelMap = Object.fromEntries(
              Object.entries(propertiesLabelToNameMap).map(([label, name]) => [
                name,
                label
              ])
            );
            const firstRowProperties = records[0]?.properties;
            const defaultSelectedFieldsLabel = new Set(
              firstRowProperties
                ? Object.keys(firstRowProperties)
                    .map(
                      (propertyName) => propertiesNameToLabelMap[propertyName]
                    )
                    .filter(Boolean)
                : []
            );
            setSelectedFieldsLabel(defaultSelectedFieldsLabel);
          }
        } else {
          showSnackbar(
            listResult.reason?.message ||
              `Failed to list Salesforce ${config.itemLabelPlural}`,
            'error'
          );
        }
      } finally {
        setLoadingProperties(false);
        setLoadingList(false);
      }
    };

    fetchData();
  }, [connectionId, importType, config, getListArgs]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (
      !connectionId ||
      !initialLoadDone ||
      debouncedSearchQuery === previousDebouncedSearchRef.current ||
      loadingAction
    ) {
      return;
    }

    const fetchData = async () => {
      setLoadingAction(true);
      setOffsetId(null);

      try {
        const searchParam = debouncedSearchQuery.trim() || null;
        const result = await config.fetchFn(
          getListArgs({ limit: 10, search: searchParam })
        );
        setItems(result.records);
        setOffsetId(result.offsetId ?? null);
        // Update the ref only after successful fetch
        previousDebouncedSearchRef.current = debouncedSearchQuery;
      } catch (e: any) {
        showSnackbar(
          e?.message || `Failed to search Salesforce ${config.itemLabelPlural}`,
          'error'
        );
        setItems([]);
        setOffsetId(null);
      } finally {
        setLoadingAction(false);
      }
    };

    fetchData();
  }, [
    debouncedSearchQuery,
    connectionId,
    config,
    getListArgs,
    initialLoadDone,
    loadingAction
  ]);

  const refresh = async () => {
    if (!connectionId) return;
    setLoadingAction(true);

    try {
      const searchParam = debouncedSearchQuery.trim() || null;
      const { records, offsetId: newOffsetId } = await config.fetchFn(
        getListArgs({ limit: 10, search: searchParam })
      );
      setItems(records);
      setOffsetId(newOffsetId ?? null);
    } catch (e: any) {
      showSnackbar(
        e?.message || `Failed to refresh Salesforce ${config.itemLabelPlural}`,
        'error'
      );
    } finally {
      setLoadingAction(false);
    }
  };

  const loadMore = async () => {
    if (!offsetId) return;
    setLoadingAction(true);

    try {
      const searchParam = debouncedSearchQuery.trim() || null;
      const { records, offsetId: newOffsetId } = await config.fetchFn(
        getListArgs({ offsetId, limit: 10, search: searchParam })
      );
      setItems((prev) => [...prev, ...records]);
      setOffsetId(newOffsetId ?? null);
    } catch (e: any) {
      showSnackbar(
        e?.message ||
          `Failed to load more Salesforce ${config.itemLabelPlural}`,
        'error'
      );
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleSelection = (itemId: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAllVisible = () => {
    const allVisibleIds = new Set(items.map((item) => item.id));
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      allVisibleIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  };

  const handleDeselectAllVisible = () => {
    const visibleIds = new Set(items.map((item) => item.id));
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      visibleIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  };

  const handleToggleField = (fieldLabel: string) => {
    setSelectedFieldsLabel((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldLabel)) {
        newSet.delete(fieldLabel);
      } else {
        newSet.add(fieldLabel);
      }
      return newSet;
    });
  };

  const handleSelectAllFields = () => {
    const allFields = Object.keys(availableProperties);
    setSelectedFieldsLabel(new Set(allFields));
  };

  const handleDeselectAllFields = () => {
    setSelectedFieldsLabel(new Set());
  };

  const handleImportSelected = async () => {
    if (selectedIds.size === 0) {
      showSnackbar(
        `Please select at least one ${config.itemLabel} to import`,
        'info'
      );
      return;
    }

    if (selectedFieldsLabel.size === 0) {
      showSnackbar('Please select at least one field to export', 'info');
      return;
    }

    setLoadingAction(true);
    try {
      const ids = Array.from(selectedIds);
      // Translate selectedFields (Set of selected field keys) into selectedPropertyNames (array of values from availableProperties for each selected field)
      const selectedPropertyNames = Array.from(selectedFieldsLabel).map(
        (selectedField) => availableProperties[selectedField]
      );

      // Fetch fresh records from backend to ensure data is up to date
      const fetchedItems =
        importType === 'ACCOUNTS'
          ? await fetchSalesforceAccountsByIds({
              accountIds: ids,
              properties: selectedPropertyNames
            })
          : await fetchSalesforceContactsByIds({
              contactIds: ids,
              properties: selectedPropertyNames
            });

      const importedRows = fetchedItems.map((item) => {
        const itemProperties = item.properties || {};
        const row: Record<string, any> = {};

        Array.from(selectedFieldsLabel).forEach((selectedField) => {
          const sfProp = availableProperties[selectedField];
          row[selectedField] = sfProp ? itemProperties[sfProp] : null;
        });

        return row;
      });

      const importConfig = {
        import_type: importType === 'ACCOUNTS' ? 'accounts' : 'contacts',
        selected_ids: ids,
        selected_properties: selectedPropertyNames
      };
      onConfirm(importedRows, importConfig);
    } catch (e: any) {
      showSnackbar(
        e?.message || `Failed to import ${config.itemLabelPlural}`,
        'error'
      );
    } finally {
      setLoadingAction(false);
    }
  };

  const visibleSelectedCount = items.filter((item) =>
    selectedIds.has(item.id)
  ).length;
  const isAllVisibleSelected =
    items.length > 0 && visibleSelectedCount === items.length;

  // Filter properties based on search query
  const filteredProperties = useMemo(() => {
    if (!fieldSearchQuery.trim()) {
      return availableProperties;
    }
    const query = fieldSearchQuery.toLowerCase();
    return Object.fromEntries(
      Object.entries(availableProperties).filter(([key, value]) =>
        key.toLowerCase().includes(query)
      )
    );
  }, [availableProperties, fieldSearchQuery]);

  const filteredPropertyKeys = Object.keys(filteredProperties);
  const orderedPropertyKeys = useMemo(() => {
    const keys = [...filteredPropertyKeys];
    return keys.sort((a, b) => {
      const aSelected = selectedFieldsLabel.has(a);
      const bSelected = selectedFieldsLabel.has(b);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.localeCompare(b);
    });
  }, [filteredPropertyKeys, selectedFieldsLabel]);
  const isAllFieldsSelected =
    filteredPropertyKeys.length > 0 &&
    filteredPropertyKeys.every((key) => selectedFieldsLabel.has(key));

  const renderSkeletonList = () => (
    <List
      dense
      sx={{
        p: 0
      }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <Box key={i} px={2} py={1.25}>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="40%" />
          {i < 5 && <Divider sx={{ mt: 1 }} />}
        </Box>
      ))}
    </List>
  );

  const renderEmptyState = () => (
    <Box
      sx={(theme) => ({
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 1,
        p: 3,
        textAlign: 'center'
      })}
    >
      <Typography variant="subtitle1" gutterBottom>
        {config.emptyTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {config.emptyMessage}
      </Typography>
      <Box display="flex" gap={1} mt={1} justifyContent="flex-end">
        {!hideCancel && (
          <Button
            variant="text"
            onClick={onCancel}
            sx={secondaryActionButtonSx}
          >
            Back
          </Button>
        )}
        <Button
          onClick={refresh}
          disabled={loadingAction}
          variant="contained"
          sx={(theme) => ({
            border: `1px solid ${theme.customColors.themePrimary}`,
            backgroundColor: theme.customColors.themePrimary,
            color: theme.customColors.themeSecondary,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:disabled': { borderColor: 'inherit' }
          })}
        >
          {loadingAction ? <CircularProgress size={18} /> : 'Refresh'}
        </Button>
      </Box>
    </Box>
  );

  const renderItemList = () => (
    <List
      dense
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        p: 0,
        borderRadius: 1
      }}
    >
      {items.map((item, idx) => {
        const isSelected = selectedIds.has(item.id);
        return (
          <React.Fragment key={item.id}>
            <ListItemButton
              disabled={loadingAction}
              onClick={() => handleToggleSelection(item.id)}
              dense
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox
                  edge="start"
                  checked={isSelected}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                id={`${importType}-${item.id}`}
                primary={config.getPrimaryText(item)}
                secondary={config.getSecondaryText(item)}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
            {idx < items.length - 1 && <Divider component="li" />}
          </React.Fragment>
        );
      })}
      {items.length === 0 && initialLoadDone && renderEmptyState()}
    </List>
  );

  const renderFieldSelectionSection = () => (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          minHeight: 28,
          cursor: 'pointer',
          backgroundColor: 'action.hover',
          '&:hover': {
            backgroundColor: 'action.selected'
          }
        }}
        onClick={() => setFieldsSectionExpanded(!fieldsSectionExpanded)}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="subtitle2" fontWeight={600}>
            Select {config.itemLabel} fields to fetch
          </Typography>
          <Tooltip
            title={
              <Typography variant="body2">
                Only selected fields will be fetched from Salesforce and these
                can be mapped to your job&apos;s input columns.
              </Typography>
            }
            arrow
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              size="small"
              sx={(theme) => ({
                p: 0.5,
                color: theme.palette.text.secondary,
                '&:hover': {
                  color:
                    theme.customColors?.themePrimary ||
                    theme.palette.primary.main
                }
              })}
              onClick={(e) => e.stopPropagation()}
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {fieldsSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
      <Collapse in={fieldsSectionExpanded}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '40vh',
            p: 1.5
          }}
        >
          {!loadingProperties && (
            <Box display="flex" gap={1} mb={1} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                onClick={
                  isAllFieldsSelected
                    ? handleDeselectAllFields
                    : handleSelectAllFields
                }
                disabled={Object.keys(availableProperties).length === 0}
                sx={{
                  textTransform: 'capitalize',
                  whiteSpace: 'nowrap',
                  minWidth: 'fit-content'
                }}
              >
                {isAllFieldsSelected ? 'Deselect All' : 'Select All'}
              </Button>
              <TextField
                size="small"
                placeholder="Search fields..."
                value={fieldSearchQuery}
                onChange={(e) => setFieldSearchQuery(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          )}
          <Box
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto'
            }}
          >
            {loadingProperties ? (
              <Box display="flex" flexDirection="column" gap={1}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} variant="text" width="100%" height={32} />
                ))}
              </Box>
            ) : filteredPropertyKeys.length === 0 ? (
              <List
                dense
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 0,
                  borderRadius: 1
                }}
              >
                <Box py={2}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {fieldSearchQuery
                      ? 'No fields match your search'
                      : 'No fields available'}
                  </Typography>
                </Box>
              </List>
            ) : (
              <>
                <List
                  dense
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 0,
                    borderRadius: 1
                  }}
                >
                  {orderedPropertyKeys.map((fieldLabel, idx) => {
                    const isSelected = selectedFieldsLabel.has(fieldLabel);
                    return (
                      <React.Fragment key={fieldLabel}>
                        <ListItemButton
                          onClick={() => handleToggleField(fieldLabel)}
                          dense
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              tabIndex={-1}
                              disableRipple
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={fieldLabel}
                            primaryTypographyProps={{
                              variant: 'body2',
                              noWrap: true
                            }}
                          />
                        </ListItemButton>
                        {idx < filteredPropertyKeys.length - 1 && (
                          <Divider component="li" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
                {selectedFieldsLabel.size > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block', flexShrink: 0 }}
                  >
                    {selectedFieldsLabel.size} field
                    {selectedFieldsLabel.size !== 1 ? 's' : ''} selected
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      maxHeight="90vh"
      sx={{
        overflow: 'hidden'
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {config.description}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            flex: 1,
            maxHeight: '50vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
                minHeight: 28,
                cursor: 'pointer',
                backgroundColor: 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected'
                }
              }}
              onClick={() => setItemsSectionExpanded(!itemsSectionExpanded)}
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Select {config.itemLabelPlural} to Import
                </Typography>
              </Box>
              {itemsSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            <Collapse in={itemsSectionExpanded}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '40vh',
                  p: 1.5
                }}
              >
                {!loadingList && (
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={1}
                    mb={1}
                    sx={{ flexShrink: 0 }}
                  >
                    <Button
                      size="small"
                      onClick={
                        isAllVisibleSelected
                          ? handleDeselectAllVisible
                          : handleSelectAllVisible
                      }
                      disabled={loadingAction}
                      sx={{
                        textTransform: 'capitalize',
                        whiteSpace: 'nowrap',
                        minWidth: 'fit-content'
                      }}
                    >
                      {isAllVisibleSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                    {importType === 'ACCOUNTS' && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterByOwner}
                            onChange={(e) => setFilterByOwner(e.target.checked)}
                            size="small"
                            disabled={loadingAction}
                          />
                        }
                        label="Only show my accounts"
                      />
                    )}
                    <TextField
                      size="small"
                      placeholder={`Search ${config.itemLabelPlural}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={loadingAction || loadingList}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                )}
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflow: 'auto'
                  }}
                >
                  {loadingList ? renderSkeletonList() : renderItemList()}
                </Box>
                {selectedIds.size > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block', flexShrink: 0 }}
                  >
                    {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''}{' '}
                    selected
                  </Typography>
                )}
                {offsetId && !loadingList && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      pt: 1
                    }}
                  >
                    <Button
                      onClick={loadMore}
                      disabled={loadingAction}
                      sx={{
                        textTransform: 'capitalize',
                        color: 'primary.main',
                        padding: '2px 8px',
                        minWidth: 'auto',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.selected'
                        }
                      }}
                    >
                      {loadingAction ? (
                        <CircularProgress
                          size={16}
                          sx={{ color: 'primary.main' }}
                        />
                      ) : (
                        'Load more'
                      )}
                    </Button>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            maxHeight: '50vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {renderFieldSelectionSection()}
        </Box>
      </Box>

      <Box
        display="flex"
        gap={1}
        justifyContent="flex-end"
        sx={{ flexShrink: 0 }}
      >
        {!hideCancel && (
          <Button
            variant="text"
            onClick={onCancel}
            disabled={loadingAction}
            sx={secondaryActionButtonSx}
          >
            Back
          </Button>
        )}
        <Button
          onClick={handleImportSelected}
          variant="contained"
          disabled={
            loadingAction ||
            selectedIds.size === 0 ||
            selectedFieldsLabel.size === 0
          }
          sx={(theme) => ({
            border: `1px solid ${theme.customColors.themePrimary}`,
            backgroundColor: theme.customColors.themePrimary,
            color: theme.customColors.themeSecondary,
            borderRadius: '50px',
            textTransform: 'capitalize',
            '&:disabled': { borderColor: 'inherit' }
          })}
        >
          {loadingAction ? (
            <CircularProgress size={18} />
          ) : (
            `Import${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ImportSalesforceList;
